import { JSX, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { asTitle } from "utils/Utils";

import { EvaluationScoresWidget } from "core/blueprint/dashboard/DashboardTypes";
import { ACHIEVED_COLOUR, resolveScore } from "evaluation/EffectiveScore";
import { ScoreAggregation } from "evaluation/ScoreAggregation";
import { Cluster, Criteria, CriteriaType } from "trajectory/TrajectoryService";

import LegendView from "./LegendView";
import PieChartView, { ChartEntry } from "./PieChartView";
import { WidgetViewProps } from "./WidgetView";

export type EvaluationScoresWidgetViewProps = WidgetViewProps<EvaluationScoresWidget>;

export default function EvaluationScoresWidgetView(
	props: Readonly<EvaluationScoresWidgetViewProps>
): JSX.Element {
	const { t } = useTranslation();

	// Which number each scored competence contributes: its latest score, or the
	// mean of all of them (#633). Absent on widgets saved before the field
	// existed, which means the latest.
	const aggregation: ScoreAggregation =
		props.widget.scoreAggregation ?? ScoreAggregation.LATEST;

	const containerRef = useRef<HTMLDivElement>(null);

	const { data, levels } = useMemo((): {
		data: Array<ChartEntry>;
		levels: number[];
	} => {
		const counted = getScores(props.evaluation.criteria);
		const { ratings, achieved } = counted;

		const scores = props.evaluation.maxScore - props.evaluation.minScore;
		const entries: Array<ChartEntry> = [];
		entries.push({
			label: asTitle(t("notRated")),
			value: isNaN(ratings[-1]) ? 0 : ratings[-1],
			colour: "#bebebe"
		});
		for (let index = 0; index <= scores; ++index) {
			const currentScore = index + props.evaluation.minScore;
			const currentName = props.evaluation.scoreNames[index];
			entries.push({
				label: currentName === "" ? index.toString() : currentName,
				value: isNaN(ratings[currentScore]) ? 0 : ratings[currentScore],
				colour: props.evaluation.scoreColours[index]
			});
		}
		// "Behaald": achieved/offered without a numeric score. Listed after the
		// score levels and excluded from the numeric average below.
		entries.push({
			label: asTitle(t("achieved")),
			value: achieved,
			colour: ACHIEVED_COLOUR
		});

		return { data: entries, levels: counted.levels };
	}, [props.evaluation, props.widget, props.asOf, aggregation, t]);

	const average: number = useMemo(() => {
		// The mean of the unrounded per-competence values (1-based levels),
		// never of the rounded buckets: averaging the buckets drifts from the
		// true mean by up to half a level under AVERAGE. Under LATEST the
		// values are whole levels, so this equals the old bucket formula.
		// "Not rated" and "behaald" competences carry no value and are
		// excluded, as before.
		if (levels.length === 0) {
			return NaN;
		}

		return levels.reduce((sum, level) => sum + level, 0) / levels.length;
	}, [levels]);

	// The average expressed as a percentage of the highest score level
	// (e.g. 2.81 of 4 levels -> 70.25%), rounded to at most two decimals.
	// data has two non-score entries (index 0 "not rated" and the trailing
	// "behaald"), so the score-level count is data.length - 2, which is also
	// the maximum value the (1-based) average can reach.
	const averagePercentage: number =
		Math.round((average / (data.length - 2)) * 100 * 100) / 100;

	// TODO: check the cluster
	function getScores(criteria: Array<Criteria>): {
		ratings: { [key: number]: number };
		achieved: number;
		levels: number[];
	} {
		const levelFilters = props.widget.cluster?.split(".");

		const level3 = criteria
			.filter(c => c.type === CriteriaType.CLUSTER)
			.filter(c => !levelFilters?.[0] || levelFilters?.[0] === c.name)
			.flatMap(c => (c as Cluster).subsections)
			.filter(c => c.type === CriteriaType.CLUSTER)
			.filter(c => !levelFilters?.[1] || levelFilters?.[1] === c.name)
			.flatMap(c => (c as Cluster).subsections)
			.filter(c => !levelFilters?.[2] || levelFilters?.[2] === c.name);

		const ratings: { [key: number]: number } = {};
		const levels: number[] = [];
		let achieved = 0;
		level3.forEach(c => {
			// The trajectory's score resolution decides whether a trailing
			// score-less "behaald" hides an earlier score or counts as
			// "Behaald"; the widget's aggregation decides which number a
			// scored competence contributes. The shared helper holds both.
			const resolved = resolveScore(
				c,
				props.evaluation.scoreResolution,
				props.asOf,
				aggregation
			);
			if (resolved.kind === "score") {
				ratings[resolved.score] =
					(isNaN(ratings[resolved.score])
						? 0
						: ratings[resolved.score]) + 1;
				// The 1-based level the legend averages, unrounded.
				levels.push(resolved.value - props.evaluation.minScore + 1);
				return;
			}

			// A "behaald"/"offered" mark counts as evaluated, so it is not
			// greyed out as "not rated".
			if (resolved.kind === "achieved") {
				achieved += 1;
				return;
			}

			ratings[-1] = (isNaN(ratings[-1]) ? 0 : ratings[-1]) + 1;
		});

		return { ratings, achieved, levels };
	}

	return (
		<>
			<Typography variant="h2" align="center">
				{props.widget.name}
			</Typography>
			{aggregation === ScoreAggregation.AVERAGE && (
				<Typography variant="subtitle1" align="center">
					{asTitle(t("scoreAggregationAverage"))}
				</Typography>
			)}
			{props.asOf !== undefined && (
				<Typography variant="subtitle1" align="center">
					{asTitle(t("asOf"))} {format(props.asOf, "dd/MM/yyyy")}
				</Typography>
			)}
			<Grid
				container
				spacing={4}
				justifyContent="center"
				alignItems="center">
				<Grid
					item
					xs={8}
					sm={6}
					md={4}
					lg={3}
					sx={{ aspectRatio: 1 }}
					ref={containerRef}>
					<PieChartView data={data} />
				</Grid>
				<Grid item xs={8} sm={6} md={5} lg={4}>
					<Grid container direction="column" spacing={1}>
						<Grid item>
							<LegendView data={data} />
						</Grid>

						{!isNaN(average) && (
							<Grid item container spacing={2}>
								<Grid item>
									<Typography variant="subtitle2">
										{asTitle(t("average"))}
									</Typography>
								</Grid>

								<Grid item>
									<Typography variant="body2">
										{data[Math.round(average)].label} (
										{average.toFixed(2)} - {averagePercentage}
										%)
									</Typography>
								</Grid>
							</Grid>
						)}
					</Grid>
				</Grid>
			</Grid>
		</>
	);
}
